import getLeanBrandsMap from "@faire/web-api/api/v2/carts/cartToken/brands/get";
import fetchCartInventoryItemMap from "@faire/web-api/api/v2/carts/cartToken/item-inventory/get";
import { IGetBrandsInCartResponse } from "@faire/web-api/faire/carts/IGetBrandsInCartResponse";
import { IItemInventoryResponse } from "@faire/web-api/faire/carts/IItemInventoryResponse";
import { makeObservable } from "@faire/web/common/makeObservable";
import { singletonGetter } from "@faire/web/common/singletons/getter";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { action, computed, observable } from "mobx";

import { CartUpdatedV2, ICartUpdateEventV2 } from "@faire/retailer/events";
import { findItemByOptionTokenAndCustomizations } from "@faire/retailer/lib/findItemByOptionTokenAndCustomizations";
import {
  getCartProductOptionQuantitiesMap,
  getCartProductQuantitiesMap,
} from "@faire/retailer/lib/getCartItemQuantitiesMap";
import { isLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { getCartToken } from "@faire/retailer/serialized-data/getCartToken";

import { ILeanCartItem } from "./consts";

/**
 * @deprecated prefer to use `useCartDataStoreV2` or `withStores` instead
 */
export class CartDataStoreV2 {
  constructor() {
    makeObservable(this);
  }

  static get = singletonGetter(CartDataStoreV2);

  @observable
  private cartItemMap: Partial<
    Record<string, IItemInventoryResponse.IInventoryItem>
  > = {};

  @observable
  private leanBrandsMap: Partial<
    Record<string, IGetBrandsInCartResponse.IBrandInfo>
  > = {};

  initialize = () => {
    if (!isLoggedInRetailer()) {
      return;
    }

    CartUpdatedV2.subscribe(this.handleCartItemsUpdateV2);

    this.fetchCartItemMap();
    this.fetchLeanBrandsMap();
  };

  fetchCartItemMap = async () => {
    try {
      const response = await fetchCartInventoryItemMap(getCartToken() ?? "");
      this.cartItemMap = response.cart_item_token_to_inventory_item ?? {};
    } catch (e) {
      //noop
    }
  };

  private fetchLeanBrandsMap = async () => {
    try {
      const response = await getLeanBrandsMap(getCartToken() ?? "");
      this.leanBrandsMap = response.brand_info;
    } catch (e) {
      //noop
    }
  };

  @action
  private handleCartItemsUpdateV2 = (event: ICartUpdateEventV2) => {
    if (!event.skipItemInventoryFetch) {
      this.fetchCartItemMap();
    }
    this.fetchLeanBrandsMap();
  };

  @computed
  get cartItems(): IItemInventoryResponse.IInventoryItem[] {
    return Object.values(this.cartItemMap).filter(isNotUndefined);
  }

  getProductTileQuantityInCart = (productToken: string) =>
    getCartProductQuantitiesMap(this.cartItems)[productToken] ?? 0;

  getItemQuantityInCart = (token: string) =>
    getCartProductOptionQuantitiesMap(this.cartItems)[token] ?? 0;

  getItemByProductOptionTokenAndCustomizations = (
    token: string,
    customizations: Map<string, string>
  ): ILeanCartItem | undefined => {
    const { cartItems } = this;
    return cartItems
      ? findItemByOptionTokenAndCustomizations(token, customizations, cartItems)
      : undefined;
  };

  getItemByToken = (cartItemToken: string): ILeanCartItem | undefined => {
    return this.cartItemMap?.[cartItemToken];
  };

  isBrandInCart = (brandToken: string) => {
    return this.leanBrandsMap?.[brandToken] !== undefined;
  };
}
